/* ==========================================================================
   signup-flow.js
   On-theme replacement for the Alia popup (campaign BAU / popup Control).

   Responsibilities, in order of execution:
     1. Eligibility  - decide whether this visitor may ever see the popup
     2. Triggers     - arm the device-appropriate triggers once eligible
     3. Step machine - run declarative action chains attached to elements
     4. Submission   - post to the signup endpoint, mirror to Omnisend + GA4

   Prizes are decided by the signup endpoint, never here. The page only holds
   board labels: one spin per visitor is fetched from the endpoint (label,
   the labels that can be won, and a signed token), kept with the saved state,
   and the email step hands the token back. The code is emailed, never shown.

   No dependency on the theme bundle, no framework, no third-party script.
   ========================================================================== */

(() => {
  const STATE_KEY = 'sf_state_v1';
  const COOKIE_STATUS = 'sf_status';
  const COOKIE_COOLDOWN = 'sf_cooldown';
  const DAY = 86400000;

  /* ----- small helpers ---------------------------------------------------- */

  const readCookie = (name) => {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  };

  const writeCookie = (name, value, maxAgeMs) => {
    if (window.Shopify?.designMode) return; // editor sessions never leave real state behind
    const parts = [
      name + '=' + encodeURIComponent(value),
      'path=/',
      'max-age=' + Math.round(maxAgeMs / 1000),
      'SameSite=Lax',
    ];
    // Share across www / apex so a subdomain hop does not re-prompt.
    const host = location.hostname.split('.').slice(-3).join('.');
    if (host.indexOf('.') !== -1) parts.push('domain=.' + host);
    if (location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  };

  const readState = () => {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      // v1 = the pre-token popup (Oct 2026); migrateState() upgrades it in place.
      return parsed && (parsed.v === 1 || parsed.v === 2) ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const writeState = (state) => {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode - cookies still carry the important flags */
    }
  };

  const UTM_KEY = 'sf_utm';
  const savedUtm = (name) => {
    try {
      return (JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}') || {})[name] || null;
    } catch (e) {
      return null;
    }
  };
  const rememberUtm = () => {
    try {
      const q = new URLSearchParams(location.search);
      if (q.get('utm_source') || q.get('utm_medium')) {
        sessionStorage.setItem(UTM_KEY, JSON.stringify({ utm_source: q.get('utm_source'), utm_medium: q.get('utm_medium') }));
      }
    } catch (e) {
      /* private mode */
    }
  };
  const once = (key) => {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      /* private mode: no dedupe, still fine */
    }
    return true;
  };

  const param = (name) => {
    try {
      return (new URLSearchParams(location.search).get(name) || '').toLowerCase();
    } catch (e) {
      return '';
    }
  };

  const csv = (value) =>
    String(value || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

  /* Cookie names are case-sensitive - never lowercase these. */
  const csvExact = (value) =>
    String(value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());

  /* Phone -> E.164. dial is like "+61", trunk is the national prefix to drop.
     Handles what shoppers actually type: "0412 345 678", "412345678",
     "61 412 345 678", "+61 0412 345 678", "0061412345678". The old version
     turned "61 412..." into +6161... (the August "+61 bug"). An AU number has
     to be a mobile; the server normalises again as a second line. */
  const toE164 = (raw, dial, trunk) => {
    let v = String(raw || '').trim().replace(/[\s().-]/g, '');
    if (v.startsWith('00')) v = '+' + v.slice(2);
    const cc = String(dial || '').replace('+', '');
    let out;
    if (v.startsWith('+')) {
      out = v;
    } else {
      v = v.replace(/\D/g, '');
      if (cc && v.startsWith(cc) && v.length > cc.length + 8) {
        out = '+' + v; // country code typed without the plus
      } else {
        if (trunk && v.startsWith(trunk) && v.length > trunk.length) v = v.slice(trunk.length);
        out = dial + v;
      }
    }
    // "+61 0412..." - the trunk zero typed after the country code
    if (cc && trunk && out.startsWith('+' + cc + trunk)) out = '+' + cc + out.slice(cc.length + 1 + trunk.length);
    if (!/^\+\d{8,15}$/.test(out)) return null;
    if (dial === '+61' && out.startsWith('+61') && !/^\+614\d{8}$/.test(out)) return null;
    return out;
  };

  const DIAL_TO_COUNTRY = { '+61': 'AU', '+64': 'NZ', '+44': 'GB', '+1': 'US', '+353': 'IE', '+65': 'SG' };

  // Same line the email step shows when the Email consent setting is blank
  const EMAIL_CONSENT_DEFAULT = 'By signing up you agree to get emails from Empress Athleticus. Unsubscribe anytime.';
  const SPIN_FRESH_MS = 24 * 3600 * 1000; // an unplayed spin is refreshed daily
  const TOKEN_SAFE_MS = 25 * DAY; // tokens live 30 days on the endpoint

  /* ----- the component ---------------------------------------------------- */

  class SignupFlow extends HTMLElement {
    connectedCallback() {
      if (this.__booted) return;
      this.__booted = true;

      const configEl = this.querySelector('[data-sf-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }

      this.steps = Array.from(this.querySelectorAll('[data-sf-step]'));
      if (!this.steps.length) return;
      rememberUtm();

      this.state = readState() || {
        v: 2,
        status: 'new',
        step: this.steps[0].dataset.sfStep,
        answer: null,
        prize: null,
        prizeToken: null,
        prizeAt: null,
        spin: null,
        spinMissed: false,
        email: null,
        phoneSubmitted: false,
        firstSeenAt: Date.now(),
        completedAt: null,
      };
      this.migrateState();

      this.dialog = this.querySelector('.sf__dialog');
      this.opened = false;

      // ?sf_preview=1 QA on the live site: open immediately, bypass
      // eligibility and triggers.
      if (param('sf_preview') === '1') {
        this.bind();
        this.open('preview');
        return;
      }

      // Theme editor: never auto-open (the overlay group renders on every
      // template, and opening on load ambushed every page being edited).
      // Open only while the Bestie popup section is selected in the sidebar,
      // and reopen after each setting-change re-render so styling stays
      // fluid. Never write real dismissal state here - closing used to lock
      // the editor out behind the 7 day cooldown.
      if (window.Shopify?.designMode) {
        this.bind();
        this.buildEditorNav();
        this.__onEditorSelect = (e) => {
          if (e.target && e.target.contains(this)) {
            this.removeReopenChip();
            this.open('editor_select');
          }
        };
        this.__onEditorDeselect = (e) => {
          if (e.target && e.target.contains(this)) {
            this.removeReopenChip();
            this.close('editor_deselect');
          }
        };
        this.__onEditorLoad = (e) => {
          if (e.target && e.target.contains(this)) this.open('editor_reload');
        };
        document.addEventListener('shopify:section:select', this.__onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.__onEditorDeselect);
        document.addEventListener('shopify:section:load', this.__onEditorLoad);
        return;
      }

      const gate = this.eligibility();
      if (!gate.ok) {
        if (once('sf_suppressed_sent')) this.track('popup_suppressed', { reason: gate.reason });
        if (this.config.debug) console.info('[signup-flow] suppressed:', gate.reason);
        return;
      }

      this.bind();
      this.track('popup_eligible', {});
      this.armTriggers();
    }

    /* ----- 1. eligibility ------------------------------------------------- */

    eligibility() {
      const c = this.config;

      if (c.isLoggedIn) return { ok: false, reason: 'logged_in' };

      const path = location.pathname.toLowerCase();
      const blocked = csv(c.blockedPaths).some((p) => path.indexOf(p) !== -1);
      if (blocked) return { ok: false, reason: 'blocked_path' };

      const cookieStatus = readCookie(COOKIE_STATUS);
      if (cookieStatus === 'completed' || cookieStatus === 'email_captured') {
        return { ok: false, reason: 'already_signed_up' };
      }
      if (this.state.status === 'completed' || this.state.status === 'email_captured') {
        return { ok: false, reason: 'already_signed_up' };
      }

      const cooldown = parseInt(readCookie(COOKIE_COOLDOWN) || '0', 10);
      if (cooldown && Date.now() < cooldown) return { ok: false, reason: 'cooldown' };

      const source = param('utm_source');
      const medium = param('utm_medium');
      if (source && csv(c.suppressUtmSource).indexOf(source) !== -1) {
        return { ok: false, reason: 'utm_source' };
      }
      if (medium && csv(c.suppressUtmMedium).indexOf(medium) !== -1) {
        return { ok: false, reason: 'utm_medium' };
      }

      // Identity cookies from an ESP mean this person is already on a list.
      // Only contact-level cookies belong here - a session cookie every
      // visitor gets (e.g. omnisendSessionID) would suppress the popup for
      // everyone.
      const espHit = csvExact(c.suppressCookies).find((name) => {
        const value = readCookie(name);
        return value !== null && value !== '' && value !== 'null';
      });
      if (espHit) return { ok: false, reason: 'esp_cookie:' + espHit };

      return { ok: true, reason: 'eligible' };
    }

    /* ----- 2. triggers ---------------------------------------------------- */

    armTriggers() {
      const c = this.config;
      const isMobile = window.matchMedia('(max-width: 740px)').matches;
      const start = () => {
        // The spin is fetched while the visitor browses, so the board already
        // shows only winnable prizes when the popup opens.
        this.ensureSpin();
        this.preload().then(() => {
          if (c.delaySeconds >= 0) {
            this.delayTimer = setTimeout(() => this.open('delay'), c.delaySeconds * 1000);
          }
          if (isMobile && c.mobileScrollUp) this.armScrollUp();
          if (!isMobile && c.desktopExitIntent) this.armExitIntent();
        });
      };

      // Never compete with LCP: on slow phones the popup used to open over a
      // page still loading, and its crown image became the page's LCP. The
      // timer now starts once the page has loaded (8 seconds at most), then
      // waits for an idle moment.
      const idle = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(start, { timeout: 3000 });
        } else {
          setTimeout(start, 1200);
        }
      };
      if (c.timerAfterLoad === false || document.readyState === 'complete') {
        idle();
      } else {
        let done = false;
        const go = () => {
          if (done) return;
          done = true;
          clearTimeout(cap);
          window.removeEventListener('load', go);
          idle();
        };
        const cap = setTimeout(go, 8000);
        window.addEventListener('load', go);
      }
    }

    /* Decode images and the display font first so the popup never paints
       half-rendered. Device-aware: 94% of traffic is mobile and never sees the
       desktop side image, so it is not fetched there. Hard timeout: a stalled
       decode must not mean the popup never shows. */
    preload() {
      const isMobile = window.matchMedia('(max-width: 740px)').matches;
      const urls = [this.config.preloadLogo, this.config.preloadBg, this.config.preloadCrown];
      if (!isMobile) urls.push(this.config.preloadSide);

      const jobs = urls.filter(Boolean).map(
        (src) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = img.onerror = resolve;
            img.src = src;
          })
      );

      if (document.fonts && this.config.fontDisplay) {
        const family = String(this.config.fontDisplay)
          .split(',')[0]
          .trim()
          .replace(/^['"]|['"]$/g, '');
        jobs.push(document.fonts.load('800 40px "' + family + '"').catch(() => {}));
      }

      if (!jobs.length) return Promise.resolve();
      return Promise.race([
        Promise.all(jobs),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
    }

    armScrollUp() {
      let lastY = window.scrollY;
      let lastT = Date.now();
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const y = window.scrollY;
          const t = Date.now();
          const dy = lastY - y; // positive = scrolling up
          const dt = t - lastT;
          if (dy > 250 && dt < 400 && y > 200) {
            this.open('scroll_up');
          }
          if (dt > 400) {
            lastY = y;
            lastT = t;
          }
        });
      };
      this.__onScroll = onScroll;
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    armExitIntent() {
      const onOut = (event) => {
        if (event.clientY <= 0 && !event.relatedTarget) this.open('exit_intent');
      };
      this.__onOut = onOut;
      document.addEventListener('mouseout', onOut);
    }

    disarm() {
      clearTimeout(this.delayTimer);
      if (this.__onScroll) window.removeEventListener('scroll', this.__onScroll);
      if (this.__onOut) document.removeEventListener('mouseout', this.__onOut);
    }

    /* ----- 3. open / close / step machine --------------------------------- */

    /* Something else owns the screen right now: the cart drawer, the early
       access gate, or another open dialog. */
    screenBusy() {
      if (document.querySelector('cart-drawer[open], .ea-gate:not([hidden]), #ea-gate')) return true;
      return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(
        (el) => !this.contains(el) && el.offsetParent !== null
      );
    }

    open(trigger) {
      if (this.opened) return;
      const automatic = trigger === 'delay' || trigger === 'scroll_up' || trigger === 'exit_intent';
      if (automatic) {
        if (this.screenBusy()) {
          clearTimeout(this.__retryOpen);
          this.__retryOpen = setTimeout(() => this.open(trigger), 2000);
          return;
        }
        // One automatic takeover per visit. Shoppers who never tap the X
        // were getting it again on every page view.
        if (!once('sf_auto_shown')) return;
      }
      const chip = document.querySelector('.sf__editor-reopen');
      if (chip) chip.remove();
      this.opened = true;
      this.__openedAt = Date.now();
      this.disarm();

      if (this.state.spin && this.state.spin.board) this.applyBoard(this.state.spin.board);
      this.ensureSpin();
      this.goToStep(this.state.step || this.steps[0].dataset.sfStep, { silent: true });
      const cur = this.querySelector('[data-sf-current]');
      if (cur && cur.querySelector('[data-sf-board]')) this.initGame(cur);
      if (cur && cur.querySelector('[data-sf-foil]')) this.initScratch(cur);

      this.setAttribute('data-sf-open', '');
      document.documentElement.style.setProperty('overflow', 'hidden');
      this.__lastFocus = document.activeElement;

      const focusable = this.focusable();
      if (focusable.length) focusable[0].focus({ preventScroll: true });
      this.__onDocKey = (event) => {
        if (event.key === 'Escape' && this.opened && !this.contains(event.target)) this.close('esc');
      };
      document.addEventListener('keydown', this.__onDocKey, true);

      this.track('popup_shown', { trigger: trigger, step: this.state.step });
    }

    close(reason) {
      if (!this.opened) return;
      this.opened = false;
      this.removeAttribute('data-sf-open');
      document.documentElement.style.removeProperty('overflow');
      if (this.__onDocKey) document.removeEventListener('keydown', this.__onDocKey, true);
      if (this.__lastFocus && this.__lastFocus.focus) this.__lastFocus.focus({ preventScroll: true });
      setTimeout(() => this.afterClose(reason), 0);
    }

    afterClose(reason) {
      // In the theme editor a close is just a peek at the page behind -
      // no cooldown, no dismissed state, and a chip to bring it back.
      if (window.Shopify?.designMode) {
        if (reason !== 'editor_deselect') this.showReopenChip();
        return;
      }

      if (this.state.status !== 'completed' && this.state.status !== 'email_captured') {
        this.state.status = 'dismissed';
        this.persist();
        writeCookie(COOKIE_COOLDOWN, String(Date.now() + this.config.cooldownDays * DAY), this.config.cooldownDays * DAY);
      }
      this.track('popup_dismissed', { step: this.state.step, reason: reason || 'close' });
    }

    removeReopenChip() {
      const chip = document.querySelector('.sf__editor-reopen');
      if (chip) chip.remove();
    }

    showReopenChip() {
      // On body, not in the component - the component is display none while
      // closed, and un-hiding it would leave an invisible scrim over the page.
      if (document.querySelector('.sf__editor-reopen')) return;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sf__editor-reopen';
      chip.textContent = 'Reopen Bestie popup';
      chip.addEventListener('click', () => this.open('editor'));
      document.body.appendChild(chip);
    }

    goToStep(name, options) {
      options = options || {};
      let target = name;
      if (name === 'next') {
        const index = this.steps.findIndex((s) => s.dataset.sfStep === this.state.step);
        const next = this.steps.slice(index + 1).find((s) => s.dataset.sfEnabled !== 'false');
        target = next ? next.dataset.sfStep : this.state.step;
      }
      const el = this.steps.find((s) => s.dataset.sfStep === target);
      if (!el) return;

      this.steps.forEach((s) => s.removeAttribute('data-sf-current'));
      el.setAttribute('data-sf-current', '');
      this.state.step = target;
      this.persist();
      this.fillTokens();
      if (el.querySelector('[data-sf-board]')) this.initGame(el);
      if (el.querySelector('[data-sf-foil]')) this.initScratch(el);
      this.syncEditorNav();

      if (!options.silent) {
        // The field if the step has one, otherwise its first button, so focus
        // never drops to the page behind the popup.
        const field =
          el.querySelector('input:not([type=hidden]):not(.sf__hp)') ||
          el.querySelector('[data-sf-primary], button:not([disabled]), a[href]');
        if (field) setTimeout(() => field.focus({ preventScroll: true }), 60);
        this.track('popup_step', { step: target });
      }
    }

    persist() {
      writeState(this.state);
    }

    disconnectedCallback() {
      this.disarm();
      document.documentElement.style.removeProperty('overflow');
      if (this.__onEditorSelect) {
        document.removeEventListener('shopify:section:select', this.__onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.__onEditorDeselect);
        document.removeEventListener('shopify:section:load', this.__onEditorLoad);
      }
    }

    /* Design mode only: pills for jumping between steps, so every screen can
       be styled without typing an email into the preview. */
    buildEditorNav() {
      if (this.querySelector('.sf__editor-nav')) return;
      const nav = document.createElement('div');
      nav.className = 'sf__editor-nav';
      this.steps.forEach((step) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = step.dataset.sfStep;
        btn.addEventListener('click', () => {
          this.goToStep(step.dataset.sfStep, { silent: true });
        });
        nav.appendChild(btn);
      });
      this.querySelector('.sf__dialog').appendChild(nav);
      this.syncEditorNav();
    }

    syncEditorNav() {
      const nav = this.querySelector('.sf__editor-nav');
      if (!nav) return;
      const current = this.state.step;
      Array.from(nav.children).forEach((btn) => {
        if (btn.textContent === current) btn.setAttribute('data-sf-on', '');
        else btn.removeAttribute('data-sf-on');
      });
    }

    /* Replace {prize} in step copy with the prize the ball landed on. */
    fillTokens() {
      const marker = String.fromCharCode(123) + 'prize' + String.fromCharCode(125);
      this.querySelectorAll('.sf__title, .sf__sub').forEach((el) => {
        if (el.dataset.sfTpl === undefined) el.dataset.sfTpl = el.textContent;
        if (el.dataset.sfTpl.indexOf(marker) !== -1) {
          el.textContent = el.dataset.sfTpl.split(marker).join(this.state.prize || 'your prize');
        }
      });
    }

    hasStep(name) {
      return this.steps.some((s) => s.dataset.sfStep === name);
    }

    focusable() {
      return Array.from(
        this.querySelectorAll(
          '[data-sf-current] button, [data-sf-current] a[href], [data-sf-current] input:not(.sf__hp), [data-sf-current] select, .sf__close'
        )
      ).filter((el) => el.offsetParent !== null);
    }

    /* ----- events --------------------------------------------------------- */

    bind() {
      this.addEventListener('click', (event) => {
        const el = event.target.closest('[data-sf-actions]');
        if (!el || !this.contains(el)) return;
        event.preventDefault();
        this.run(el);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close('esc');
          return;
        }
        if (event.key !== 'Tab') return;
        const items = this.focusable();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      // Enter inside a field runs that field's submit button.
      this.addEventListener('keypress', (event) => {
        if (event.key !== 'Enter') return;
        const input = event.target.closest('input');
        if (!input) return;
        const step = input.closest('[data-sf-step]');
        const submit = step && step.querySelector('[data-sf-primary]');
        if (submit) {
          event.preventDefault();
          this.run(submit);
        }
      });
    }

    /* Declarative action chains: data-sf-actions='[{"type":"goToStep",...}]' */
    async run(el) {
      let chain;
      try {
        chain = JSON.parse(el.dataset.sfActions);
      } catch (e) {
        return;
      }
      if (el.getAttribute('aria-busy') === 'true') return;

      for (const action of chain) {
        const fn = this.ACTIONS[action.type];
        if (!fn) continue;
        const ok = await fn.call(this, action, el);
        if (ok === false) return; // validation or network failure stops the chain
      }
    }

    get ACTIONS() {
      return {
        submitAnswer: function (action) {
          this.state.answer = action.value;
          this.persist();
          this.track('quiz_answered', { answer: action.value });
          return true;
        },

        goToStep: function (action) {
          // A known subscriber is routed to the welcome back screen instead of
          // the success screen - they are already on the list, and the mystery
          // discount is for new besties only.
          var target = action.step || 'next';
          if (this.state.known && action.ifKnown && this.hasStep(action.ifKnown)) {
            target = action.ifKnown;
          }
          this.goToStep(target);
          return true;
        },

        closePopup: function () {
          this.close('button');
          return true;
        },

        playGame: async function (action, el) {
          if (this.__playing) return false;
          const step = el.closest('[data-sf-step]');
          if (!this.visibleSlots(step).length) return false;
          this.__playing = true;
          el.setAttribute('aria-busy', 'true');

          // The endpoint picks the prize; the drop is theatre onto that slot.
          const won = await this.claimSpin(step);
          if (!won) {
            el.removeAttribute('aria-busy');
            this.__playing = false;
            this.missSpin();
            this.goToStep('next');
            return true;
          }
          const slots = this.visibleSlots(step);
          let winner = this.slotFor(slots, won.label);
          if (winner === -1) {
            // The board and the prize table word it differently. Land on a
            // slot showing the real prize, never on the wrong one.
            winner = Math.floor(Math.random() * slots.length);
            slots[winner].textContent = won.label;
          }

          await this.dropBall(step, winner);

          slots.forEach((s, i) => {
            if (i === winner) s.setAttribute('data-sf-won', '');
            else s.removeAttribute('data-sf-won');
          });
          this.keepPrize(won);
          this.track('game_prize', { prize: this.state.prize });

          await new Promise((r) => setTimeout(r, 1100));
          el.removeAttribute('aria-busy');
          this.__playing = false;
          this.goToStep('next');
          return true;
        },

        openUrl: function (action) {
          if (!action.url) return true;
          window.open(action.url, action.target || '_self');
          return true;
        },

        trackEvent: function (action) {
          this.track(action.name, action.payload || {});
          return true;
        },

        skipStep: function (action) {
          this.track('phone_skipped', { step: this.state.step });
          this.goToStep(action.step || 'next');
          return true;
        },

        submitEmail: async function (action, el) {
          const step = el.closest('[data-sf-step]');
          const input = step.querySelector('[data-sf-email]');
          const value = (input.value || '').trim();

          if (!isEmail(value)) return this.fail(step, this.config.text.emailError);
          if (this.botDetected(step)) return this.fail(step, this.config.text.genericError);

          this.busy(el, true);
          const res = await this.post({
            type: 'email',
            email: value,
            interest: this.state.answer,
            prizeToken: this.state.prizeToken || null,
            spinMissed: (!this.state.prizeToken && this.state.spinMissed) || undefined,
            consentText: this.consentFor(step, this.config.consentEmailText, EMAIL_CONSENT_DEFAULT),
          });
          this.busy(el, false);
          if (!res.ok) return this.fail(step, this.config.text.genericError);

          var known = !!(res.data && res.data.known === true);
          this.state.email = value;
          this.state.phoneToken = (res.data && res.data.phoneToken) || null;
          this.state.known = known;
          // Either way this visitor is on the list and must never be prompted
          // again. A known contact has no phone step to complete, so they are
          // done here.
          this.state.status = known ? 'completed' : 'email_captured';
          if (known) this.state.completedAt = Date.now();
          this.persist();
          writeCookie(COOKIE_STATUS, this.state.status, 400 * DAY);
          this.identify({ email: value });
          this.track('email_submitted', { interest: this.state.answer, known: known });
          return true;
        },

        submitPhone: async function (action, el) {
          const step = el.closest('[data-sf-step]');
          const input = step.querySelector('[data-sf-phone]');
          const select = step.querySelector('[data-sf-country]');
          const consent = step.querySelector('[data-sf-consent-check]');
          const parts = (select ? select.value : '+61|0').split('|');
          const phone = toE164(input.value, parts[0], parts[1]);

          if (!phone) return this.fail(step, this.config.text.phoneError);
          if (consent && !consent.checked) return this.fail(step, this.config.text.consentError);
          if (this.botDetected(step)) return this.fail(step, this.config.text.genericError);

          this.busy(el, true);
          const res = await this.post({
            type: 'phone',
            email: this.state.email,
            phone: phone,
            interest: this.state.answer,
            // The wording sits above the button (and the tickbox, when it is
            // switched on, has been ticked): tapping it is express consent.
            smsConsent: true,
            phoneToken: this.state.phoneToken || null,
            countryCode: DIAL_TO_COUNTRY[parts[0]] || 'AU',
            consentText: this.consentFor(step, this.config.consentSmsText),
          });
          this.busy(el, false);
          if (!res.ok) return this.fail(step, this.config.text.genericError);

          this.state.phoneSubmitted = true;
          this.persist();
          this.identify({ email: this.state.email, phone: phone });
          this.track('phone_submitted', {});
          return true;
        },

        /* Pick a crown: the tapped crown always holds the weighted prize. */
        pickCrown: async function (action, el) {
          if (this.__playing) return false;
          const step = el.closest('[data-sf-step]');
          this.__playing = true;

          const won = await this.claimSpin(step);
          if (!won) {
            this.__playing = false;
            this.missSpin();
            this.goToStep('next');
            return true;
          }
          const label = won.label;

          const cards = Array.from(step.querySelectorAll('[data-sf-card]'));
          cards.forEach((c) => {
            c.disabled = true;
            if (c !== el) c.setAttribute('data-sf-lost', '');
          });
          const reveal = el.querySelector('[data-sf-reveal]');
          if (reveal) reveal.textContent = label;
          el.setAttribute('data-sf-won', '');

          this.keepPrize(won);
          this.track('game_prize', { prize: label, opener: 'crowns' });

          await new Promise((r) => setTimeout(r, 1400));
          this.__playing = false;
          this.goToStep('next');
          return true;
        },

        /* Scratch card reveal - fired by the button, or automatically once
           enough of the foil has been scrubbed away. */
        scratchReveal: async function (action, el) {
          if (this.__playing) return false;
          const step = el.closest('[data-sf-step]');
          this.__playing = true;

          const won = await this.claimSpin(step);
          if (!won) {
            this.__playing = false;
            this.missSpin();
            this.goToStep('next');
            return true;
          }
          const label = won.label;

          const reveal = step.querySelector('[data-sf-reveal]');
          if (reveal) reveal.textContent = label;
          const foil = step.querySelector('[data-sf-foil]');
          if (foil) foil.setAttribute('data-sf-cleared', '');
          step.setAttribute('data-sf-revealed', '');

          this.keepPrize(won);
          this.track('game_prize', { prize: label, opener: 'scratch' });

          await new Promise((r) => setTimeout(r, 1400));
          this.__playing = false;
          this.goToStep('next');
          return true;
        },

        complete: function () {
          this.state.status = 'completed';
          this.state.completedAt = Date.now();
          this.persist();
          writeCookie(COOKIE_STATUS, 'completed', 400 * DAY);
          this.track('completed', {
            interest: this.state.answer,
            phone: this.state.phoneSubmitted,
          });
          return true;
        },
      };
    }

    /* ----- prizes (decided by the endpoint) ------------------------------ */

    /* Saved state from the pre-token popup (Oct 2026) or from a layout that
       has changed since: drop what no longer applies. A prize won on the old
       popup carried no token, so that visitor plays again for a real one. */
    migrateState() {
      const st = this.state;
      const captured = st.status === 'email_captured' || st.status === 'completed';
      if ('prizeCode' in st) delete st.prizeCode;
      if (!captured && st.prize && !st.prizeToken) {
        st.prize = null;
        st.step = this.steps[0].dataset.sfStep;
      }
      if (!captured && st.prizeToken && Date.now() - (st.prizeAt || 0) > TOKEN_SAFE_MS) {
        st.prize = null;
        st.prizeToken = null;
        st.step = this.steps[0].dataset.sfStep;
      }
      if (!this.steps.some((s) => s.dataset.sfStep === st.step)) st.step = this.steps[0].dataset.sfStep;
      st.v = 2;
    }

    hasGame() {
      return !!this.querySelector('[data-sf-slot]');
    }

    /* One spin per visitor: fetched once, kept with the saved state until it
       is played (or a day passes unplayed), so a page reload never re-rolls.
       Resolves to { label, token, board } or null. */
    ensureSpin(force) {
      if (window.Shopify?.designMode || !this.hasGame() || this.state.prizeToken || this.state.spinMissed) {
        return Promise.resolve(null);
      }
      const saved = this.state.spin;
      if (!force && saved && saved.token && Date.now() - (saved.at || 0) < SPIN_FRESH_MS) {
        return Promise.resolve(saved);
      }
      if (this.__spinPromise && !force) return this.__spinPromise;
      this.__spinPromise = this.post({ type: 'spin' }, 8000).then((res) => {
        this.__spinPromise = null;
        const data = res.ok && res.data;
        if (!data || !Array.isArray(data.board)) return null;
        this.applyBoard(data.board);
        if (!data.prize || typeof data.prize.label !== 'string') return null;
        this.state.spin = { label: data.prize.label, token: data.prize.token, board: data.board, at: Date.now() };
        this.persist();
        return this.state.spin;
      });
      return this.__spinPromise;
    }

    /* The prize for this play. In the theme editor a random board label, so
       every screen can be previewed without touching the endpoint. */
    async claimSpin(step) {
      if (window.Shopify?.designMode) {
        const slots = this.visibleSlots(step);
        const pick = slots[Math.floor(Math.random() * slots.length)];
        return pick ? { label: pick.textContent.trim(), token: null } : null;
      }
      let won = await this.ensureSpin();
      if (!won) won = await this.ensureSpin(true); // one retry covers a prefetch that failed
      return won || null;
    }

    keepPrize(won) {
      this.state.prize = won.label;
      this.state.prizeToken = won.token || null;
      this.state.prizeAt = Date.now();
      this.state.spin = null;
      this.state.spinMissed = false;
      this.persist();
    }

    /* The spin never came back (timeout, network, the endpoint saying no).
       Never strand the shopper on the game: go on to the email step, and the
       endpoint rolls their prize when the email arrives. */
    missSpin() {
      this.state.prize = null;
      this.state.prizeToken = null;
      this.state.spin = null;
      this.state.spinMissed = true;
      this.persist();
      this.track('spin_missed', {});
    }

    /* Hide board labels the endpoint cannot pay out (odds 0 or not in the
       prize table): a prize on show must be winnable. If nothing matches, the
       labels and the table disagree; leave the board alone rather than empty it. */
    applyBoard(board) {
      if (!Array.isArray(board) || !board.length) return;
      const live = board.map((l) => String(l).trim().toLowerCase());
      const slots = Array.from(this.querySelectorAll('[data-sf-slot]'));
      const matches = slots.filter((s) => live.indexOf(s.textContent.trim().toLowerCase()) !== -1);
      if (!matches.length) {
        if (this.config.debug) console.warn('[signup-flow] no board label matches the prize table', board);
        return;
      }
      slots.forEach((s) => {
        s.hidden = live.indexOf(s.textContent.trim().toLowerCase()) === -1;
      });
      this.steps.forEach((step) => {
        if (step.querySelector('[data-sf-board]')) this.initGame(step);
      });
    }

    visibleSlots(step) {
      return Array.from(step.querySelectorAll('[data-sf-slot]')).filter((s) => !s.hidden);
    }

    /* The board slot showing this label, or -1 when the board and the prize
       table word it differently. */
    slotFor(slots, label) {
      const want = String(label).trim().toLowerCase();
      const i = slots.findIndex((s) => s.textContent.trim().toLowerCase() === want);
      if (i === -1 && this.config.debug) console.warn('[signup-flow] no board slot for prize', label);
      return i;
    }

    /* Scratch foil: a canvas the shopper scrubs away. Enough scrubbing fires
       the same reveal as the button. */
    initScratch(step) {
      const canvas = step.querySelector('[data-sf-foil]');
      if (!canvas || canvas.__init) return;
      canvas.__init = true;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.parentElement.clientWidth || 220;
      const h = canvas.parentElement.clientHeight || 90;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const foil = ctx.createLinearGradient(0, 0, w, h);
      foil.addColorStop(0, '#cfc4cf');
      foil.addColorStop(0.4, '#f2eaf0');
      foil.addColorStop(0.7, '#b9aab9');
      foil.addColorStop(1, '#e8dfe6');
      ctx.fillStyle = foil;
      ctx.fillRect(0, 0, w, h);

      let scrubbed = 0;
      let last = null;
      const scrub = (x, y) => {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, 16, 0, Math.PI * 2);
        ctx.fill();
        if (last) scrubbed += Math.hypot(x - last.x, y - last.y);
        last = { x: x, y: y };
        if (scrubbed > 340) {
          const btn = step.querySelector('[data-sf-primary]');
          if (btn && !this.__playing) this.run(btn);
        }
      };
      const pos = (e) => {
        const r = canvas.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };
      canvas.addEventListener('pointerdown', (e) => {
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch (err) {
          /* synthetic or already-released pointers */
        }
        last = null;
        const p = pos(e);
        scrub(p.x, p.y);
      });
      canvas.addEventListener('pointermove', (e) => {
        if (e.buttons !== 1) return;
        e.preventDefault();
        const p = pos(e);
        scrub(p.x, p.y);
      });
    }

    /* ----- ball drop board -------------------------------------------------- */

    initGame(step) {
      const canvas = step.querySelector('[data-sf-board]');
      if (!canvas) return;
      const slots = this.visibleSlots(step).length;
      const board = canvas.parentElement;
      if (slots < 2) {
        // One prize left: no pegs to bounce through, and no empty board either.
        board.style.display = 'none';
        canvas.__sfGame = null;
        return;
      }
      board.style.removeProperty('display');
      const rows = Math.max(1, slots - 1);
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = canvas.parentElement.clientWidth || 340;
      const cssHeight = rows * 42 + 40;
      canvas.width = cssWidth * dpr;
      canvas.height = cssHeight * dpr;
      canvas.style.height = cssHeight + 'px';
      const g = {
        ctx: canvas.getContext('2d'),
        dpr: dpr,
        w: cssWidth,
        h: cssHeight,
        rows: rows,
        slotW: cssWidth / slots,
        topY: 26,
        rowH: 42,
        ballSize: this.config.ballSize || 16,
        ballImg: null,
        hits: [],
        trail: [],
      };
      if (this.config.ballImage) {
        const img = new Image();
        img.onload = () => {
          g.ballImg = img;
        };
        img.src = this.config.ballImage;
      }
      canvas.__sfGame = g;
      this.drawBoard(g, null);
    }

    pegs(g) {
      const out = [];
      for (let r = 0; r < g.rows; r++) {
        const count = r + 2;
        for (let j = 0; j < count; j++) {
          out.push({
            x: g.w / 2 + (j - (count - 1) / 2) * g.slotW,
            y: g.topY + r * g.rowH,
          });
        }
      }
      return out;
    }

    drawBoard(g, ball) {
      const ctx = g.ctx;
      const styles = getComputedStyle(this);
      ctx.setTransform(g.dpr, 0, 0, g.dpr, 0, 0);
      ctx.clearRect(0, 0, g.w, g.h);
      ctx.fillStyle = styles.getPropertyValue('--sf-text').trim() || '#fff';
      ctx.globalAlpha = 0.45;
      this.pegs(g).forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      if (g.hits && g.hits.length) {
        // expanding, fading ring on every peg the ball has just struck
        const accent = styles.getPropertyValue('--sf-accent').trim() || '#D31A63';
        const now = Date.now();
        g.hits = g.hits.filter((hit) => now - hit.at < 300);
        g.hits.forEach((hit) => {
          const age = (now - hit.at) / 300;
          ctx.globalAlpha = (1 - age) * 0.9;
          ctx.strokeStyle = accent;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(hit.x, hit.y, 4 + age * 8, 0, Math.PI * 2);
          ctx.stroke();
        });
        ctx.globalAlpha = 1;
      }
      if (g.trail && g.trail.length) {
        const accent = styles.getPropertyValue('--sf-accent').trim() || '#D31A63';
        g.trail.forEach((p, i) => {
          const k = (i + 1) / g.trail.length;
          ctx.globalAlpha = 0.32 * k;
          ctx.fillStyle = accent;
          ctx.beginPath();
          ctx.arc(p.x, p.y, (g.ballSize / 2) * (0.3 + 0.45 * k), 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.globalAlpha = 1;
      }
      if (ball) {
        if (g.ballImg) {
          // brand coin or crown, spinning as it falls, aspect preserved
          const img = g.ballImg;
          const scale = g.ballSize / Math.max(img.naturalWidth, img.naturalHeight);
          const bw = img.naturalWidth * scale;
          const bh = img.naturalHeight * scale;
          ctx.save();
          ctx.translate(ball.x, ball.y);
          ctx.rotate(ball.rot || 0);
          ctx.drawImage(img, -bw / 2, -bh / 2, bw, bh);
          ctx.restore();
        } else {
          ctx.fillStyle = styles.getPropertyValue('--sf-accent').trim() || '#D31A63';
          ctx.beginPath();
          ctx.arc(ball.x, ball.y, g.ballSize / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    /* Animate a real-feeling drop: the ball falls ONTO a peg each row (the
       lattice guarantees a head-on strike every half-slot step), kicks off it,
       and the last row of pegs sits directly above the slot centres. Gravity
       easing on falls, decelerating hops, a flash on every struck peg and a
       settle bounce in the slot. Driven by wall-clock time over setTimeout
       frames, so the landing slot and total duration hold at any frame rate;
       instant under prefers-reduced-motion. */
    dropBall(step, winner) {
      const canvas = step.querySelector('[data-sf-board]');
      const g = canvas && canvas.__sfGame;
      if (!g) return Promise.resolve();

      // The destination is fixed by the weighted pick; the ROUTE is theatre.
      // Most drops open with a wrong-way excursion, wander back, and save a
      // correcting kick for the last row so the ball tips in at the buzzer.
      // Corner slots have no slack for drama - every kick is needed.
      const rights = winner;
      const lefts = g.rows - winner;
      const slack = Math.min(rights, lefts);
      let decisions = [];
      if (slack > 0 && Math.random() < 0.7) {
        const majorDir = rights >= lefts ? 1 : -1;
        const major = Math.max(rights, lefts);
        const excursion = 1 + Math.floor(Math.random() * slack);
        for (let i = 0; i < excursion; i++) decisions.push(-majorDir);
        const middle = [];
        for (let i = 0; i < major - 1; i++) middle.push(majorDir);
        for (let i = 0; i < slack - excursion; i++) middle.push(-majorDir);
        for (let i = middle.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          const t = middle[i];
          middle[i] = middle[j];
          middle[j] = t;
        }
        decisions = decisions.concat(middle);
        decisions.push(majorDir); // the last-second tip into the winning slot
      } else {
        for (let i = 0; i < g.rows; i++) decisions.push(i < winner ? 1 : -1);
        for (let i = decisions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          const t = decisions[i];
          decisions[i] = decisions[j];
          decisions[j] = t;
        }
      }

      const contactY = (row) => g.topY + row * g.rowH;
      const points = [{ x: g.w / 2, y: 6, kind: 'fall' }];
      let x = g.w / 2;
      let sum = 0; // running position in half-slot steps
      for (let r = 0; r < g.rows; r++) {
        sum += decisions[r];
        x += decisions[r] * (g.slotW / 2);
        const py = contactY(r);
        const edge = Math.abs(sum) === r + 1; // outermost peg of this row
        const inward = edge ? -Math.sign(sum) : 0;
        // head-on strike - on edge pegs the ball sits on the inner shoulder,
        // never outside the pyramid silhouette
        points.push({
          x: x + inward * 3,
          y: py - g.ballSize / 2 - 2,
          kind: 'contact',
          pegX: x,
          pegY: py,
        });
        const nextDir = r < g.rows - 1 ? decisions[r + 1] : 0;
        const flipped = nextDir !== 0 && nextDir !== decisions[r];
        if (edge && nextDir !== 0 && !flipped) {
          // continuing down the outside: a low staircase roll that hugs the
          // edge instead of swinging out past it
          points.push({ x: x + inward * 3, y: py - g.rowH * 0.26, kind: 'hop' });
        } else {
          // ...a kick up and toward where it is heading next; a change of
          // direction ricochets higher than a same-way roll
          points.push({
            x: x + nextDir * (flipped ? 10 : 7),
            y: py - g.rowH * (flipped ? 0.55 : 0.38),
            kind: 'hop',
          });
        }
      }
      // straight down into the slot, with a settle bounce
      points.push({ x: x, y: g.h - 10, kind: 'contact' });
      points.push({ x: x, y: g.h - 22, kind: 'hop' });
      points.push({ x: x, y: g.h - 10, kind: 'end' });

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.drawBoard(g, points[points.length - 1]);
        return Promise.resolve();
      }

      // time budget per segment follows its length, so hops are quick and
      // falls take the time gravity deserves
      const spans = [];
      let total = 0;
      for (let i = 1; i < points.length; i++) {
        const d = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
        const w = Math.pow(Math.max(d, 4), 0.8);
        spans.push(w);
        total += w;
      }
      const totalMs = Math.max(600, (this.config.dropSeconds || 2.5) * 1000);
      const ends = [];
      let acc = 0;
      spans.forEach((w) => {
        acc += (w / total) * totalMs;
        ends.push(acc);
      });

      g.trail = [];
      g.hits = [];
      let lastSeg = -1;

      return new Promise((resolve) => {
        const t0 = Date.now();
        const tick = () => {
          const elapsed = Math.min(Date.now() - t0, totalMs);
          let i = ends.findIndex((e) => elapsed <= e);
          if (i === -1) i = ends.length - 1;

          // register every peg strike crossed since the last frame
          for (let sIdx = lastSeg + 1; sIdx <= i; sIdx++) {
            const arrived = points[sIdx];
            if (arrived && arrived.kind === 'contact' && arrived.pegX !== undefined) {
              g.hits.push({ x: arrived.pegX, y: arrived.pegY, at: Date.now() });
            }
          }
          lastSeg = Math.max(lastSeg, i);

          const segStart = i === 0 ? 0 : ends[i - 1];
          const local = Math.min(1, (elapsed - segStart) / (ends[i] - segStart));
          const a = points[i];
          const b = points[i + 1];
          // gravity: accelerate into contacts, decelerate out of hops
          const easedY = b.kind === 'hop' ? 1 - (1 - local) * (1 - local) : local * local;
          const easedX = local * local * (3 - 2 * local);
          const pos = {
            x: a.x + (b.x - a.x) * easedX,
            y: a.y + (b.y - a.y) * easedY,
            rot: (elapsed / totalMs) * Math.PI * 4,
          };
          g.trail.push({ x: pos.x, y: pos.y });
          if (g.trail.length > 14) g.trail.shift();
          this.drawBoard(g, pos);
          if (elapsed < totalMs) setTimeout(tick, 16);
          else {
            // final contact flash + clean rest frame
            const floor = points[points.length - 1];
            g.trail = [];
            this.drawBoard(g, floor);
            setTimeout(() => {
              g.hits = [];
              this.drawBoard(g, floor);
              resolve();
            }, 300);
          }
        };
        tick();
      });
    }

    /* ----- 4. submission + instrumentation -------------------------------- */

    async post(payload, timeoutMs) {
      const body = Object.assign(
        {
          v: 2,
          source: 'onsite_popup',
          campaign: this.config.campaign,
          consentAt: new Date().toISOString(),
          pageUrl: location.href,
          utmSource: param('utm_source') || savedUtm('utm_source'),
          utmMedium: param('utm_medium') || savedUtm('utm_medium'),
        },
        payload
      );

      // Theme editor previews must never create real contacts.
      if (window.Shopify?.designMode) {
        return { ok: true, data: { known: false } };
      }

      // A cold serverless start plus Omnisend and Shopify can take a while;
      // giving up early would show an error for a signup that went through.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs || 20000);
      try {
        // Plain text is a "simple" cross-site request, so the browser skips the extra
        // permission check (preflight) it sends first for JSON. The endpoint reads either.
        const res = await fetch(this.config.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          credentials: 'omit',
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (!res.ok) {
          if (this.config.debug) console.warn('[signup-flow] endpoint error', res.status);
          return { ok: false };
        }
        return { ok: true, data: await res.json().catch(() => ({})) };
      } catch (e) {
        if (this.config.debug) console.warn('[signup-flow] endpoint unreachable', e);
        return { ok: false };
      } finally {
        clearTimeout(timer);
      }
    }

    /* Stitches this browser session to the Omnisend contact so browse and
       abandoned-cart automations keep working. Consent + properties are set
       server side - the web SDK cannot do that reliably. */
    identify(ids) {
      if (window.Shopify?.designMode) return;
      try {
        if (typeof window.omnisend === 'undefined') return;
        window.omnisend.push([
          'identifyContact',
          Object.assign({ tags: ['onsite-signup'] }, ids),
        ]);
      } catch (e) {
        /* never let analytics break the flow */
      }
    }

    track(name, payload) {
      const data = Object.assign({ event: 'signup_' + name, sf_campaign: this.config.campaign }, payload);
      // Editor sessions are design work, not shopper behaviour - keep them out
      // of the funnel data entirely.
      if (window.Shopify?.designMode) {
        if (this.config.debug) console.info('[signup-flow]', name, payload);
        return;
      }
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data);
      } catch (e) {
        /* noop */
      }
      // GA4 gets these through the Google tag already on the page (the Google
      // & YouTube app's), so popup events share the shopper's own GA4 session.
      // They used to go out a second time through a Shopify custom pixel,
      // whose sandbox counted every popup as a separate visitor. Events from
      // before the tag has loaded wait for it (30 seconds at most).
      const params = Object.assign({ sf_campaign: this.config.campaign }, payload);
      this.gaSend('signup_' + name, params);
      try {
        if (window.omnisend && name === 'completed') {
          window.omnisend.push(['track', 'onsite_signup_completed', { interest: this.state.answer }]);
        }
      } catch (e) {
        /* noop */
      }
      this.dispatchEvent(new CustomEvent('signup-flow:' + name, { bubbles: true, detail: payload }));
      if (this.config.debug) console.info('[signup-flow]', name, payload);
    }

    /* The consent record is the wording the shopper actually saw: the line
       rendered in this step, or failing that the setting. */
    consentFor(step, configured, fallback) {
      const el = step && step.querySelector('.sf__consent');
      const seen = el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
      if (seen) return seen;
      if (configured && String(configured).trim()) return String(configured).trim();
      return fallback || null;
    }

    gaSend(eventName, params) {
      const ready = () => typeof window.gtag === 'function' && !!window.google_tag_manager;
      const send = (n, p) => {
        try {
          window.gtag('event', n, p);
        } catch (e) {
          /* never let analytics break the flow */
        }
      };
      if (ready()) {
        send(eventName, params);
        return;
      }
      (this.__gaQueue = this.__gaQueue || []).push([eventName, params]);
      if (this.__gaTimer) return;
      const started = Date.now();
      this.__gaTimer = setInterval(() => {
        if (!ready() && Date.now() - started < 30000) return;
        clearInterval(this.__gaTimer);
        this.__gaTimer = null;
        const queue = this.__gaQueue || [];
        this.__gaQueue = [];
        if (ready()) queue.forEach((q) => send(q[0], q[1]));
      }, 500);
    }

    /* ----- ui helpers ----------------------------------------------------- */

    fail(step, message) {
      const box = step.querySelector('[data-sf-error]');
      if (box) {
        box.textContent = message;
        box.setAttribute('data-sf-visible', '');
      }
      return false;
    }

    busy(el, isBusy) {
      const step = el.closest('[data-sf-step]');
      const box = step && step.querySelector('[data-sf-error]');
      if (box) box.removeAttribute('data-sf-visible');
      el.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      if (isBusy) {
        el.dataset.sfLabel = el.textContent;
        el.textContent = this.config.text.working;
      } else if (el.dataset.sfLabel) {
        el.textContent = el.dataset.sfLabel;
      }
    }

    /* Honeypot + time-on-form. Bots fill hidden fields and submit instantly. */
    botDetected(step) {
      const pot = step.querySelector('.sf__hp input');
      if (pot && pot.value) return true;
      return Date.now() - (this.__openedAt || 0) < 1200;
    }
  }

  if (!customElements.get('signup-flow')) {
    customElements.define('signup-flow', SignupFlow);
  }
})();
